import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import FormioTabRenderer from './FormioTabRenderer';
import { TABS, GUEST_FORM_IDS, countFormPanels } from './guestFormConfig';
import { calculateVisibleFieldProgress } from '../../utils/formVisibleProgress';
import UserFormPrototypeHeader, { tabPercentFromProgress } from '../user/UserFormPrototypeHeader';
import '../css/GuestEdit.css';
import '../css/UserFormHeader.css';
/* UserFormView.css loaded via FormioOverrides (FormioTabRenderer) — avoid re-import wipe */

// Map formId -> tab so prefetched schemas can be stored per tab.
const TAB_BY_FORM_ID = Object.fromEntries(
  Object.entries(GUEST_FORM_IDS).map(([tab, id]) => [id, tab])
);

/** Personal Registration-style tabs for a guest (no Guest tab). */
const GUEST_PERSONAL_TABS = [
  { key: 'registration', label: 'Registration', id: GUEST_FORM_IDS.REGISTRATION },
  { key: 'accreditation', label: 'Accreditation', id: GUEST_FORM_IDS.ACCREDITATION },
  { key: 'travel', label: 'Travel', id: GUEST_FORM_IDS.TRAVEL },
  { key: 'accommodation', label: 'Accommodation', id: GUEST_FORM_IDS.ACCOMMODATION },
];

// Guest forms: show errors inline under each field, not as a top summary alert.
// highlightErrors:false is REQUIRED — highlightErrors:true makes Form.io
// eagerly validate every form on mount, so switching tabs highlights all empty
// required fields on the other tabs (cross-tab bleed). With it off, fields only
// get `.is-invalid` after that tab is actually submitted; guest CSS reveals the
// message via :has(.is-invalid).
const GUEST_FORM_OPTIONS = { noAlerts: true, highlightErrors: false };

const GuestEdit = () => {
  const { participantId, guestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || '';
  const isUserPortal = location.pathname.startsWith('/user/');
  const listBackPath = isUserPortal ? '/user/guest-registration' : '/ems/participants';
  const listBackLabel = isUserPortal ? '← Guest Registration' : '← Participants';

  const [guest, setGuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS[0]);
  // Tabs the user has actually tried to Save with errors. Form.io flags empty
  // required fields eagerly on mount, so we only reveal those errors on a tab
  // the user submitted — not on tabs they merely switched to.
  const [erroredTabs, setErroredTabs] = useState(() => new Set());
  const [submissionVersion, setSubmissionVersion] = useState(0);
  const [participantName, setParticipantName] = useState('');
  // Schemas per tab (state, not ref) so completion dots recompute once loaded.
  const [tabSchemas, setTabSchemas] = useState({});
  /** Live form_data by tab — drives real filled/total (same util as user portal). */
  const [formDataByTab, setFormDataByTab] = useState({});

  const latestFormDataRef = useRef({});
  const flushLiveFormDataRef = useRef(null);
  const triggerSaveRef = useRef(null);
  const triggerFormSubmitRef = useRef(null);
  const clearFormErrorsRef = useRef(null);
  const closeAfterSaveRef = useRef(false);
  const tabLiveDataRef = useRef({});
  const savedTimerRef = useRef(null);
  const schemaCacheRef = useRef({});

  // Fetch guest on mount
  useEffect(() => {
    if (!participantId || !guestId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/ems/participants/${participantId}/guests/${guestId}`)
      .then((res) => {
        if (cancelled) return;
        setGuest(res.data);
        const fd = res.data?.form_data && typeof res.data.form_data === 'object'
          ? res.data.form_data
          : {};
        latestFormDataRef.current = fd;
        setFormDataByTab(fd);
        setSubmissionVersion((v) => v + 1);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Failed to load guest');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId, guestId, API_URL]);

  // Fetch participant name once guest loads
  useEffect(() => {
    if (!participantId) return;
    axios
      .get(`${API_URL}/ems/participants/${participantId}`)
      .then((res) => {
        const pd = res.data?.person_data || {};
        const name = [pd.givenFirstName, pd.familyLastName].filter(Boolean).join(' ');
        if (name) setParticipantName(name);
      })
      .catch(() => {});
  }, [participantId, API_URL]);

  // Pre-fetch all tab schemas once guest is loaded so tab switches are instant
  useEffect(() => {
    if (!guest) return;
    Object.values(GUEST_FORM_IDS).forEach(async (formId) => {
      if (schemaCacheRef.current[formId]) return;
      try {
        const res = await axios.get(`${API_URL}/ems/forms/${formId}`);
        if (res.data?.schema?.components) {
          schemaCacheRef.current[formId] = res.data.schema;
          const tab = TAB_BY_FORM_ID[formId];
          if (tab) setTabSchemas((prev) => ({ ...prev, [tab]: res.data.schema }));
        }
      } catch (_) {}
    });
  }, [guest, API_URL]);

  // Warn on browser unload when dirty
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Ctrl+S keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup saved timer on unmount
  useEffect(() => {
    return () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); };
  }, []);

  const initialSubmission = useMemo(() => {
    const tabData = { ...(guest?.form_data?.[activeTab] || {}) };
    if (activeTab === 'REGISTRATION') {
      if (!tabData.givenFirstName && guest?.firstName)   tabData.givenFirstName = guest.firstName;
      if (!tabData.familyLastName && guest?.lastName)    tabData.familyLastName = guest.lastName;
      if (!tabData.emailAddress   && guest?.email)       tabData.emailAddress   = guest.email;
    }
    return { data: tabData };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest, activeTab]);

  const handleFormChange = useCallback((tab, payload) => {
    // FormioTabRenderer emits { data: formValues } — unwrap like ParticipantEdit.
    const fields =
      payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload || {};
    const nextTabData = { ...(latestFormDataRef.current[tab] || {}), ...fields };
    latestFormDataRef.current = {
      ...latestFormDataRef.current,
      [tab]: nextTabData,
    };
    setFormDataByTab((prev) => ({ ...prev, [tab]: nextTabData }));
    setIsDirty(true);
  }, []);

  // Persists the active tab. Called by FormioTabRenderer ONLY after Form.io
  // validation passes, so blank required fields block the save.
  const persistActiveTab = useCallback(async (data) => {
    if (!guest) return false;
    try {
      setSaving(true);
      setError(null);
      const existing = guest.form_data || {};
      const merged = { ...existing };
      // Other tabs: carry their pending edits (validated only when their own tab saves).
      for (const tab of TABS) {
        if (tab !== activeTab && latestFormDataRef.current[tab]) {
          merged[tab] = { ...(existing[tab] || {}), ...latestFormDataRef.current[tab] };
        }
      }
      // Active tab: use the validated submission data from Form.io.
      merged[activeTab] = { ...(existing[activeTab] || {}), ...data };

      const res = await axios.put(
        `${API_URL}/ems/participants/${participantId}/guests/${guestId}`,
        { form_data: merged }
      );
      setGuest(res.data);
      const savedFd = res.data?.form_data || {};
      latestFormDataRef.current = savedFd;
      setFormDataByTab(savedFd);
      tabLiveDataRef.current = {};
      // Saved cleanly → drop this tab's error-reveal flag.
      setErroredTabs((prev) => {
        if (!prev.has(activeTab)) return prev;
        const next = new Set(prev);
        next.delete(activeTab);
        return next;
      });
      setIsDirty(false);
      setSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 2500);
      if (closeAfterSaveRef.current) {
        closeAfterSaveRef.current = false;
        navigate(-1);
      }
      return true;
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save guest');
      return false;
    } finally {
      setSaving(false);
    }
  }, [guest, activeTab, API_URL, participantId, guestId, navigate]);

  // Validation failed: flag this tab so its inline errors are revealed, and
  // cancel any pending "save & close" so the page doesn't navigate away.
  const handleValidationErrors = useCallback(() => {
    closeAfterSaveRef.current = false;
    setErroredTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      return new Set(prev).add(activeTab);
    });
  }, [activeTab]);

  // Save = run Form.io submit (validate). On success persistActiveTab fires;
  // on failure handleValidationErrors fires. No silent save of blank required.
  const handleSave = useCallback(async () => {
    if (typeof clearFormErrorsRef.current === 'function') clearFormErrorsRef.current();
    if (typeof triggerFormSubmitRef.current === 'function') {
      await triggerFormSubmitRef.current();
    }
  }, []);

  const handleSaveAndClose = useCallback(async () => {
    closeAfterSaveRef.current = true;
    await handleSave();
  }, [handleSave]);

  const handleCancel = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) return;
    navigate(-1);
  };

  const guestLabel = guest?.firstName || guest?.email || 'Guest';
  const guestLastName = guest?.lastName || '';
  const activeFormId = GUEST_FORM_IDS[activeTab];
  const guestPortalSummary = useMemo(() => {
    if (!guest) return { participant: null, person: null };
    return {
      person: {
        name: guest.firstName || guestLabel,
        lname: guestLastName,
        email: guest.email || '',
      },
      participant: {
        registration_status: 'Guest',
        approval_status: 'Pending',
        person_data: {
          givenFirstName: guest.firstName || '',
          familyLastName: guest.lastName || '',
          emailAddress: guest.email || '',
          ...(guest.meta && typeof guest.meta === 'object' ? guest.meta : {}),
        },
      },
    };
  }, [guest, guestLabel, guestLastName]);

  /** Same filled/total math as UserFormView panel rings (visible leaf fields). */
  const guestTabProgress = useMemo(() => {
    const progress = {};
    for (const tab of GUEST_PERSONAL_TABS) {
      const emsKey = tab.key.toUpperCase();
      const schema = tabSchemas[emsKey];
      const data = formDataByTab[emsKey] || guest?.form_data?.[emsKey] || {};
      if (!schema?.components) {
        progress[tab.key] = { completed: 0, total: 0 };
        continue;
      }
      const result = calculateVisibleFieldProgress(data, schema.components, null);
      progress[tab.key] = {
        completed: result.filled,
        total: result.total,
      };
    }
    return progress;
  }, [tabSchemas, formDataByTab, guest]);

  const activeSkeletonPanels = useMemo(() => {
    const schema = tabSchemas[activeTab];
    return countFormPanels(schema?.components);
  }, [tabSchemas, activeTab]);

  if (loading) {
    return (
      <div className={isUserPortal ? 'user-layout user-form-prototype' : 'guest-edit-page'}>
        <div className="guest-edit-loading">
          <Spinner animation="border" size="sm" style={{ color: '#2563EB' }} />
          <span>Loading guest…</span>
        </div>
      </div>
    );
  }

  if (error && !guest) {
    return (
      <div className={isUserPortal ? 'user-layout user-form-prototype' : 'guest-edit-page'}>
        <div className="guest-edit-error" role="alert">{error}</div>
        <button className="guest-edit-btn guest-edit-btn--cancel" onClick={() => navigate(listBackPath)}>
          {listBackLabel}
        </button>
      </div>
    );
  }

  const formBody = (
    <>
      {error && (
        <div className="guest-edit-error" role="alert" aria-live="assertive">
          {error}
        </div>
      )}

      <div
        key={`${guestId}-${activeTab}-v${submissionVersion}`}
        className={`guest-form-tab guest-form-body-enter guest-form-tab-${activeTab}${erroredTabs.has(activeTab) ? ' guest-form-tab--show-errors' : ''}`}
      >
        <FormioTabRenderer
          formId={activeFormId}
          initialSchema={schemaCacheRef.current[activeFormId] || null}
          submission={initialSubmission}
          submissionVersion={submissionVersion}
          onDataChange={(data) => handleFormChange(activeTab, data)}
          API_URL={API_URL}
          liveDataRef={tabLiveDataRef}
          flushLiveFormDataRef={flushLiveFormDataRef}
          triggerSaveRef={triggerSaveRef}
          triggerFormSubmitRef={triggerFormSubmitRef}
          clearFormErrorsRef={clearFormErrorsRef}
          onFormSave={persistActiveTab}
          onValidationErrors={handleValidationErrors}
          formOptions={GUEST_FORM_OPTIONS}
          skeletonPanels={activeSkeletonPanels}
          showProgressBadges
        />
      </div>

      <div className="guest-edit-actions">
        <span role="status" aria-live="polite" className="visually-hidden">
          {saved ? 'Guest form saved successfully.' : ''}
        </span>

        <button
          type="button"
          className="guest-edit-btn guest-edit-btn--cancel"
          onClick={handleCancel}
          disabled={saving}
          title="Discard changes and go back"
        >
          Cancel
        </button>

        <button
          type="button"
          className={`guest-edit-btn guest-edit-btn--save${saved ? ' saved' : ''}`}
          onClick={handleSave}
          disabled={saving}
          title="Save changes (Ctrl+S)"
        >
          {saving
            ? <><Spinner animation="border" size="sm" />Saving…</>
            : saved ? '✓ Saved'
            : 'Save'}
        </button>

        <button
          type="button"
          className="guest-edit-btn guest-edit-btn--primary"
          onClick={handleSaveAndClose}
          disabled={saving}
          title="Save and return"
        >
          {saving
            ? <><Spinner animation="border" size="sm" />Saving…</>
            : 'Save & Close'}
        </button>
      </div>
    </>
  );

  if (isUserPortal) {
    return (
      <div className="user-layout user-form-prototype">
        <button
          type="button"
          className="guest-edit-breadcrumb"
          onClick={() => navigate(listBackPath)}
        >
          {listBackLabel}
        </button>

        <UserFormPrototypeHeader
          activeSegment={activeTab.toLowerCase()}
          canSwitchTabs
          isAthlete={false}
          tabs={GUEST_PERSONAL_TABS}
          onTabSelect={(tab) => setActiveTab(tab.key.toUpperCase())}
          participant={guestPortalSummary.participant}
          person={guestPortalSummary.person}
          tabProgress={guestTabProgress}
        />

        <div className="user-form-scroll">
          {formBody}
        </div>
      </div>
    );
  }

  return (
    <div className="guest-edit-page">
      <button
        type="button"
        className="guest-edit-breadcrumb"
        onClick={() => navigate(listBackPath)}
      >
        {listBackLabel}
      </button>

      <div className="guest-edit-header">
        <div className="guest-edit-header__left">
          <h4 className="guest-edit-title">
            {participantName ? `Guest of ${participantName} — ${guestLabel}` : `Edit Guest — ${guestLabel}`}
          </h4>
          <p className="guest-edit-subtitle">Update guest information across registration, travel, and accommodation.</p>
        </div>
      </div>

      <div className="guest-edit-tabs">
        <div role="tablist" aria-label="Guest form sections" className="guest-edit-tabs__track">
          {TABS.map((tab) => {
            const isActive = activeTab === tab;
            // Source of truth: same filled/total % badge as the user-side form tabs.
            const realPct = tabPercentFromProgress(guestTabProgress[tab.toLowerCase()]);
            const displayPct = realPct == null ? 0 : realPct;
            const isComplete = realPct === 100;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`guest-edit-tab${isActive ? ' guest-edit-tab--active' : ''}${isComplete ? ' guest-edit-tab--complete' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
                <span className="guest-edit-tab__pct" aria-label={`${displayPct} percent complete`}>
                  {displayPct}%
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="user-form-scroll">
        {formBody}
      </div>
    </div>
  );
};

export default GuestEdit;
